/**
 * Guide · How it works — the centerpiece. Explains how the analysis screens
 * fit together over one shared, read-only snapshot: from the whole portfolio
 * on the velocity field, to the pick-face ceiling in Scenarios, to the two
 * exportable review lists. Every figure is computed from the snapshot; the
 * screens only read and export, never write back.
 */

import { GuidePage } from "@/features/guide/guide-page";

interface HowStep {
  title: string;
  body: string;
  meta: string;
}

const STEPS: HowStep[] = [
  {
    title: "One shared snapshot",
    body: "Every screen reads the same site-level snapshot — each SKU held at the UKDC, its velocity band, Rx net sales or Dx inventory, and the site's pick-face ceiling through 2029. Because they share one source, a decision on one screen is consistent with the figures on the next.",
    meta: "The @/lib/data barrel — the one swap point for a real backend feed",
  },
  {
    title: "See the whole portfolio, and test it against the ceiling",
    body: "Scenarios plots every held SKU by how fast it moves against the money or space it consumes, and stacks the held total against the site's pick-face ceiling year by year to 2029. Pull a lever — relocate, pack rationalise, Rx→Dx convert, add capacity, or pace the NPI rollout — and watch both the field and the ceiling react live.",
    meta: "/scenarios",
  },
  {
    title: "Turn it into a list",
    body: "Location review ranks the SKUs that belong in a lane or automation; Non-movers surfaces those that haven't shipped but still hold a face. Accept or remove each row, and export the decided set as CSV — the non-mover list can feed straight back into the relocation lever.",
    meta: "/reports/storage · /reports/nonmover",
  },
  {
    title: "Read-only, computed from the data",
    body: "Every figure on every screen is computed from the shared snapshot — nothing is invented. The screens look and export; they never edit, delete, or write anything back into the source.",
    meta: "Read-only, always",
  },
];

export function GuideHowItWorksPage() {
  return (
    <GuidePage
      eyebrow="How it works"
      title="Three screens, one shared snapshot."
      intro="Vision Intelligence takes you from the whole UKDC portfolio to an actionable list without leaving one consistent dataset. This page walks through how the three screens fit together — from the velocity field and the pick-face ceiling on Scenarios, to the two review lists you can accept, remove, and export."
      cta={{ to: "/scenarios", label: "Start on Scenarios" }}
    >
      <section className="border-border bg-card rounded-lg border shadow-sm">
        <header className="border-border border-b px-5 py-4">
          <h2 className="text-foreground text-sm font-semibold">
            From portfolio to actionable list
          </h2>
          <p className="text-muted mt-1 text-xs leading-relaxed">
            How the three screens hand off to each other over one shared snapshot.
          </p>
        </header>
        <ol className="divide-border divide-y">
          {STEPS.map((step, i) => (
            <li key={step.title} className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 px-5 py-4">
              <span className="bg-primary text-primary-foreground inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-foreground text-sm font-medium">{step.title}</p>
                <p className="text-muted mt-1 text-sm leading-relaxed">{step.body}</p>
                <p className="text-warm-gray-06 mt-1.5 text-xs">{step.meta}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </GuidePage>
  );
}
